import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import AuthService from "../services/authService";
import jwt_decode from 'jwt-decode';
import LoggingService from "../services/loggingService";
import { MessageType } from "../enums/messageType";
import { IToken } from "../models/IToken";
import { IUserDetail } from "../models/IUserDetail";
import { app, authentication, HostClientType } from "@microsoft/teams-js";
import SecurityService from "../services/securityService";
import ApplicationSettingService from "../services/applicationSettingService";
import { CONSTANTS } from "../constants/constants";
import { IdentityProvider } from "../enums/identityProvider";
import SessionService from "../services/sessionService";
import TimekeeperService from "../services/timekeeperService";

export interface IAuthResult {
  success: boolean;
  errorType?: 'userNotFound' | 'schemaRefreshRunning' | 'authFailed' | 'unknown';
  errorMessage?: string;
}

interface AuthContextType {
  userToken: string | undefined;
  logout: () => void;
  login: () => Promise<any>;
  popupLogin: () => Promise<any>;
  loginCallback: () => Promise<any>;
  popupLoginCallback: () => Promise<any>;
  silentCallback: () => Promise<any>;
  redirectUserToIdp: () => Promise<boolean>;
  getToken: (code : string) => Promise<boolean>;
  getAccessTokenFromRefreshToken: (code : string) => Promise<boolean>;
  clearToken: () => void;
  allRegions: { [key: string]: string }[];
  setUserRegion:(region:string)=> void;
  identityServerUrl: string;
  //Platform Identity support
  identityProvider: IdentityProvider;
  platformIdentityServer: string;
  redirectToPlatformIdentity: (email: string, orgReference: string) => Promise<IAuthResult>;
  handlePlatformIdentityCallback: (piToken: string) => Promise<boolean>;
}

let AuthContext = createContext<AuthContextType>(null!);

const useAuth = () => useContext(AuthContext);

let eventInitialized = false;

 // This function is used to get the user id from the token
 function getUserId(accessToken: string): string {
  let userId = "";
  try {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - getUserId()` : '', MessageType.Info);
    if (accessToken) {
      let decodedToken: any = jwt_decode(accessToken);
      userId = decodedToken.unique_name;
    }
  } catch (error) {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - getUserId() - ${error}` : '', MessageType.Error);
  } finally {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - getUserId() - output(${userId})` : '', MessageType.Info);
  }
  return userId;
}

function storeLastLoginEmailFromSession(): void {
  try {
    const email = sessionStorage.getItem(CONSTANTS.userName);
    if (email) {
      localStorage.setItem(CONSTANTS.lastLoginEmail, email);
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Stored last login email (${email})` : '', MessageType.Info);
    }
  } catch (error) {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - storeLastLoginEmailFromSession error - ${error}` : '', MessageType.Error);
  }
}

function AuthProvider({ children }: { children: React.ReactNode }) {

  let accessToken: string = sessionStorage.getItem("X-Auth-Token")! ;

  let [userToken, setUser] = useState<string | undefined>(accessToken);

  let [allRegions, setAllRegions] = useState<{ [key: string]: string }[]>([]);

  let [userRegion, setRegion] = useState<string>(localStorage.getItem(CONSTANTS.user_region) ?? '');

  let [identityServerUrl, setIdentityServerUrl] = useState<string>('');

  //Platform Identity state
  let [identityProvider, setIdentityProvider] = useState<IdentityProvider>(
    (sessionStorage.getItem("IdentityProvider") as IdentityProvider) || IdentityProvider.IDS
  );
  let [platformIdentityServer, setPlatformIdentityServer] = useState<string>('');

  useEffect(() => {
      setAllRegions(ApplicationSettingService.getAllRegions());
      if(userRegion){ 
          setIdentityServerUrl(ApplicationSettingService.getIdentityServerEndpoint(userRegion));
          
          //Load Platform Identity configuration
          ApplicationSettingService.getPlatformIdentityServerEndpoint(userRegion).then((piServer) => {
            setPlatformIdentityServer(piServer);
          }).catch((e) => {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI config not available for region ${userRegion}` : '', MessageType.Info);
          });
      }
  }, [userRegion]);

  if (!eventInitialized)
  {
    eventInitialized = true;
    
    AuthService.userManager().events.addAccessTokenExpiring(function () {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - token expiring` : '', MessageType.Info);
    });

    AuthService.userManager().events.addAccessTokenExpired(function () {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - token expired` : '', MessageType.Info);
    });
    
    AuthService.userManager().events.addSilentRenewError(function (e : any) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - silent renew error - ${e.message}` : '', MessageType.Error);
    });
    
    AuthService.userManager().events.addUserLoaded(function (authUser : any) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - user loaded event raised - ${JSON.stringify(authUser)}` : '', MessageType.Info);

        if (authUser){
          LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Updating token via user loaded event.` : '', MessageType.Info);
          sessionStorage.setItem("X-Auth-Token", authUser.access_token);
          sessionStorage.setItem("refreshToken", authUser.refresh_token!);
          sessionStorage.setItem("userId", getUserId(authUser.access_token));
          storeLastLoginEmailFromSession();
          setUser(authUser.access_token);
        }
    });
  }

  const logout = async () => {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Logout Started` : '', MessageType.Info);

    const currentProvider = sessionStorage.getItem("IdentityProvider");
    
    try {
        const hostClientType = sessionStorage.getItem('hostClientType');
        const oauthRedirectMethod = hostClientType === HostClientType.web ? 'page' : 'deeplink';
        const appInternalId = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_internal_id);

        const baseDomain = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_url) || window.location.origin;
        const normalizedBase = baseDomain.replace(/\/$/, '');
        if (currentProvider === IdentityProvider.PI) {
          //Platform Identity logout
          LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI Logout` : '', MessageType.Info);
                              
          // Use Teams popup for logout (same as login)
          const orgReference = sessionStorage.getItem("UserOrgReference") || '';
          const logoutCallbackUrl = `${window.location.origin}/index.html#/pi-logout-callback`;
          const logoutUrl = `${platformIdentityServer}/auth/logout?organizationHint=${encodeURIComponent(orgReference)}`;

          const logoutStartUrl = `${window.location.origin}/index.html#/auth-start?oauthRedirectMethod=${oauthRedirectMethod}&hostRedirectUrl=${encodeURIComponent(logoutCallbackUrl)}&authId=${appInternalId}&identityServerUrl=${encodeURIComponent(logoutUrl)}&identityProvider=${IdentityProvider.PI}`;

          // Use Teams popup authentication (same as login)
          // Cleanup will happen in PlatformIdentityLogoutCallback after logout completes
          app.initialize().then(() => {
            authentication.authenticate({
              url: logoutStartUrl,
              width: 500,
              height: 300,
            }).then(() => {
              LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI Logout completed` : '', MessageType.Info);
              // Navigate to login redirect page after logout completes
              SessionService.clearUserSession();
              clearToken();
              TimekeeperService.clearCache();
              setTimeout(() => {
                window.location.hash = "/loginRedirect";
              }, 500);
            }).catch((err) => {
              LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI Logout error: ${err}` : '', MessageType.Error);
              // If logout popup fails, clean up anyway
              SessionService.clearUserSession();
              clearToken();
              TimekeeperService.clearCache();
              // Navigate to login redirect page even on error
              setTimeout(() => {
                window.location.hash = "/loginRedirect";
              }, 500);
            });
          });
        } else {
          //IDS logout
        const authorityUrl = identityServerUrl;
        const clientId = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_client_id);
        const postLogoutRedirectUri = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_url);
        let token=sessionStorage.getItem("X-Auth-Token")!;
        

        const logoutUrl = `${authorityUrl}connect/endsession?client_id=${clientId}&post_logout_redirect_uri=${postLogoutRedirectUri}&id_token_hint=${token}`;
    
        const logoutWindow = window.open(logoutUrl, '_blank', 'width=500,height=300');

        if (hostClientType == HostClientType.web){  
            const checkWindowClosed = setInterval(() => {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Logout Interval Started` : '', MessageType.Info);

            try {
                  logoutWindow!.close();
                  clearInterval(checkWindowClosed);
                  sessionStorage.removeItem("X-Auth-Token");
                  sessionStorage.removeItem("refreshToken");
                  sessionStorage.removeItem("userId");
                  clearToken();
                  TimekeeperService.clearCache();

                  LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Logout Interval Ended` : '', MessageType.Info);
            } catch (e) {
              LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Logout Interval Error - ${e}` : '', MessageType.Error);
            }
            }, 2000);
        }
        else {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Clearing session on Logout` : '', MessageType.Info);
            sessionStorage.removeItem("X-Auth-Token");
            sessionStorage.removeItem("refreshToken");
            sessionStorage.removeItem("userId");
            clearToken();
            TimekeeperService.clearCache();
          }
        }
    } catch (error) {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Logout Error - ${error}` : '', MessageType.Error);
    }

    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Logout Ended` : '', MessageType.Info);
  };

  const login = () =>  AuthService.userManager().signinRedirect();

  const popupLogin = async () => {
    return AuthService.userManager().signinPopup().then(function(authUser : any) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Popup Sign-in response success - ${JSON.stringify(authUser)}` : '', MessageType.Info);
        if (authUser){
          LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Updating token after Popup sign-in.` : '', MessageType.Info);
          sessionStorage.setItem("X-Auth-Token", authUser.access_token);
          sessionStorage.setItem("refreshToken", authUser.refresh_token!);
          sessionStorage.setItem("userId", getUserId(authUser.access_token));
          storeLastLoginEmailFromSession();
          setUser(authUser.access_token);
        }
    }).catch(function(err : any) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Popup Sign-in response error - ${err}` : '', MessageType.Error);
    });
  };

  const loginCallback = async () => {
    let url = window.location.hash;
    url = url.replace('#', '');
    return AuthService.userManager().signinRedirectCallback(url).then(function(authUser : any) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Sign-in response success - ${JSON.stringify(authUser)}` : '', MessageType.Info);
        if (authUser){
          LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Updating token after sign-in.` : '', MessageType.Info);
          sessionStorage.setItem("X-Auth-Token", authUser.access_token);
          sessionStorage.setItem("refreshToken", authUser.refresh_token!);
          sessionStorage.setItem("userId", getUserId(authUser.access_token));
          storeLastLoginEmailFromSession();
          setUser(authUser.access_token);
        }
    }).catch(function(err : any) {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Sign-in response error - ${err}` : '', MessageType.Error);
    });
  };
  
  const popupLoginCallback = async () => {
      let url = window.location.hash;
      url = url.replace('#', '');
    return AuthService.userManager().signinPopupCallback(url).then(function() {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Sign-in callback response success` : '', MessageType.Info);
    }).catch(function(err: any) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Sign-in callback response error - ${err}` : '', MessageType.Error);
    });
  };

  const silentCallback = async () => {
    let url = window.location.hash;
    url = url.replace('#', '');
    return AuthService.userManager().signinSilentCallback(url).then(function() {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Sign-in silent callback response success` : '', MessageType.Info);
    }).catch(function(err: any) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Sign-in silent callback response error - ${err}` : '', MessageType.Error);
    });
  };

  const redirectUserToIdp = async () => {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Redirecting user to Idp` : '', MessageType.Info);
    
    var oauthRedirectMethod = 'not-supported';
    var oauthRedirectUrl = encodeURIComponent(window.location.origin + "/index.html#/auth-end");
    var hostClientType = sessionStorage.getItem('hostClientType');
    var appInternalId = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_internal_id);

    if (hostClientType === HostClientType.web){
      oauthRedirectMethod = 'page';
    }
    else{
      oauthRedirectMethod = 'deeplink';
    }
    
    return app.initialize().then(() => {
      AuthService.updateUserManagerSettings(identityServerUrl);

      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Redirect user to Idp - Initialize - ${oauthRedirectMethod} - ${oauthRedirectUrl} - ${identityServerUrl}` : '', MessageType.Info);

      return authentication.authenticate({
        url: `${window.location.origin}/index.html#/auth-start?oauthRedirectMethod=${oauthRedirectMethod}&hostRedirectUrl=${oauthRedirectUrl}&authId=${appInternalId}&identityServerUrl=${identityServerUrl}`,
        isExternal: true,
        width: 600,
        height: 550,
      }).then((result) => {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Redirect user to Idp - Auth Code received` : '', MessageType.Info);
        return getToken(result).then((tokenSuccess) => {
          if (tokenSuccess){
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Sign-in successful.` : '', MessageType.Info);
          }
          return tokenSuccess;
        });
      }).catch(function(err) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Redirect user to Idp response error - ${err}` : '', MessageType.Error);
        throw err;
      });
    })
  };

  const getToken = async (code: string) => {  
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Get token started` : '', MessageType.Info);

    const appClientId: string = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_client_id);
    const getTokenUrl: string = `${identityServerUrl}connect/token`;
    const redirectUrl: string = `${window.location.origin}/index.html#/auth-end`;

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    }

    var details : any = {
      'client_id': appClientId,
      'code': code,
      'grant_type': 'authorization_code',
      'redirect_uri': redirectUrl,
      'code_verifier': 'c775e7b757ede630cd0aa1113bd102661ab38829ca52a6422ab782862f268646'
    };

    var formBody = [];
    for (var property in details) {
      var encodedKey = encodeURIComponent(property);
      var encodedValue = encodeURIComponent(details[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }

    var obj = formBody.join("&");

    const response = await fetch(getTokenUrl, {
        method: 'POST',
        body: obj,
        headers: headers
    });

    if (response.ok) {
      var data : IToken = await response.json() as IToken;  
      
      var userId = getUserId(data.access_token);
      sessionStorage.setItem("X-Auth-Token", data.access_token);
      sessionStorage.setItem("userId", userId);
      let hostClientType = sessionStorage.getItem('hostClientType');
      if (hostClientType !== HostClientType.web) {
        sessionStorage.setItem("refreshToken", data.refresh_token);
      }

      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Get token received for ${userId}` : '', MessageType.Info);

      storeLastLoginEmailFromSession();
      setUser(data.access_token);
      return true;
    }
    else
    {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Get token error - ${JSON.stringify(response)}` : '', MessageType.Error);
      return true;
    }
  };

  const getAccessTokenFromPIRefreshToken = async (refreshToken: string) => {
    console.log('[AuthContext] getAccessTokenFromPIRefreshToken() started');
    console.log('[AuthContext] RefreshToken provided:', !!refreshToken, refreshToken ? `Length: ${refreshToken.length}` : 'null');
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Refreshing PI token started` : '', MessageType.Info);
  
    try {
      let orgReference = sessionStorage.getItem("UserOrgReference") || '';
      console.log('[AuthContext] OrgReference from sessionStorage:', orgReference || 'not found');
      
      // Call our server-side refresh endpoint which handles PI /auth/refresh server-to-server
      const region = localStorage.getItem(CONSTANTS.user_region);
      const piTokenUrl = await ApplicationSettingService.getPITokenUrl(region);
      const refreshEndpoint = piTokenUrl ? `${piTokenUrl}token/refresh` : `${window.location.origin}/api/token/refresh`;
      console.log('[AuthContext] Calling server-side refresh endpoint:', refreshEndpoint);

      const tokenResponse = await fetch(refreshEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platformIdentityServer: platformIdentityServer,
          organizationHint: orgReference,
          refreshToken: refreshToken
        })
      });
      console.log('[AuthContext] Refresh endpoint response status:', tokenResponse.status, tokenResponse.statusText);
      
      if (tokenResponse.ok) {
        const tokenDetail: any = await tokenResponse.json();
        console.log('[AuthContext] Token API response:', {
          hasToken: !!tokenDetail?.token,
          hasRefreshToken: !!tokenDetail?.refreshToken,
          tokenLength: tokenDetail?.token?.length || 0
        });
        
        if (tokenDetail && tokenDetail.token) {
          console.log('[AuthContext] Validating PI token...');
          const userDetail = await SessionService.validatePlatformIdentityUser(tokenDetail.token);
          console.log('[AuthContext] Validation result:', {
            hasTcToken: !!userDetail?.tcToken,
            hasEmail: !!userDetail?.email,
            hasOrgReference: !!userDetail?.orgReference,
            userNotFound: userDetail?.userNotFound || false
          });
          
          if (userDetail && userDetail.userNotFound) {
            console.error('[AuthContext] User not found after validation');
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - User not found after PI refresh` : '', MessageType.Error);
            return false;
          }

          if (userDetail && userDetail.schemaRefreshRunning) {
            console.error('[AuthContext] Schema refresh is running');
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Schema refresh is running after PI refresh` : '', MessageType.Info);
            return false;
          }
          
          if (userDetail && userDetail.tcToken && !userDetail.userNotFound) {
            console.log('[AuthContext] Storing tokens in sessionStorage...');
            sessionStorage.setItem("piToken", tokenDetail.token);
            sessionStorage.setItem("PI-X-Auth-Token", tokenDetail.token);
            sessionStorage.setItem("X-Auth-Token", userDetail.tcToken);
            
            if (tokenDetail.refreshToken) {
              sessionStorage.setItem("refreshToken", tokenDetail.refreshToken);
              console.log('[AuthContext] New refreshToken stored');
            } else {
              console.warn('[AuthContext] No refreshToken in response');
            }
            
            if (userDetail.orgReference) {
              sessionStorage.setItem("UserOrgReference", userDetail.orgReference);
            }
    
            var userId = getUserId(userDetail.tcToken);
            sessionStorage.setItem("userId", userId);
            console.log('[AuthContext] UserId:', userId);
            
            if (userDetail.email) {
              sessionStorage.setItem("UserName", userDetail.email);
            }
    
            console.log('[AuthContext] Refresh successful - tokens updated for user:', userId);
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - New PI and TC tokens stored for ${userId}` : '', MessageType.Info);
            
            setUser(userDetail.tcToken);
            SecurityService.loadPermissionsAndLicenses();
            return true;
          } else {
            console.error('[AuthContext] Failed to get TC token from validation', {
              hasUserDetail: !!userDetail,
              hasTcToken: !!userDetail?.tcToken,
              userNotFound: userDetail?.userNotFound
            });
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Failed to get TC token from validation` : '', MessageType.Error);
            return false;
          }
        } else {
          console.error('[AuthContext] No PI token in response:', JSON.stringify(tokenDetail));
          LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - No PI token in response. Response: ${JSON.stringify(tokenDetail)}` : '', MessageType.Error);
          return false;
        }
      } else {
        const errorText = await tokenResponse.text();
        console.error('[AuthContext] Token API error:', tokenResponse.status, errorText);
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Token API error - ${tokenResponse.status}: ${errorText}` : '', MessageType.Error);
        return false;
      }
    } catch (error) {
      console.error('[AuthContext] PI refresh token exception:', error);
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI refresh token exception - ${error}` : '', MessageType.Error);
      return false;
    } finally {
      console.log('[AuthContext] getAccessTokenFromPIRefreshToken() completed');
    }
  };

  const getAccessTokenFromIDSRefreshToken = async (refreshToken: string) => {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Refreshing IDS token started` : '', MessageType.Info);
  
    const appClientId: string = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_client_id);
    const tokenUrl: string = `${identityServerUrl}connect/token`;
  
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    };
  
    var details: any = {
      'client_id': appClientId,
      'refresh_token': refreshToken,
      'grant_type': 'refresh_token'
    };
  
    var formBody = [];
    for (var property in details) {
      var encodedKey = encodeURIComponent(property);
      var encodedValue = encodeURIComponent(details[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }
    var body = formBody.join("&");
  
    const response = await fetch(tokenUrl, {
      method: 'POST',
      body: body,
      headers: headers
    });
  
    if (response.ok) {
      var data: IToken = await response.json() as IToken;
      
      sessionStorage.setItem("X-Auth-Token", data.access_token);
      if (data.refresh_token) {
        sessionStorage.setItem("refreshToken", data.refresh_token);
      }
  
      var userId = getUserId(data.access_token);
      sessionStorage.setItem("userId", userId);
  
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - New IDS token received for ${userId}` : '', MessageType.Info);
      
      storeLastLoginEmailFromSession();
      setUser(data.access_token);

      SecurityService.loadPermissionsAndLicenses();
      return true;
    } else {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - IDS refresh token error - ${JSON.stringify(response)}` : '', MessageType.Error);
      return false;
    }
  }; 

  const getAccessTokenFromRefreshToken = async (refreshToken: string) => {
    console.log('[AuthContext] getAccessTokenFromRefreshToken() started');
    console.log('[AuthContext] RefreshToken provided:', !!refreshToken, refreshToken ? `Length: ${refreshToken.length}` : 'null');
    
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Refreshing token started` : '', MessageType.Info);
    
    const currentProvider = sessionStorage.getItem("IdentityProvider") as IdentityProvider;
    console.log('[AuthContext] Current identity provider:', currentProvider);
    
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Current identity provider: ${currentProvider}` : '', MessageType.Info);
    
    if (currentProvider === IdentityProvider.PI) {
      console.log('[AuthContext] Routing to PI refresh flow');
      const result = await getAccessTokenFromPIRefreshToken(refreshToken);
      console.log('[AuthContext] PI refresh result:', result);
      return result;
    } else {
      console.log('[AuthContext] Routing to IDS refresh flow');
      const result = await getAccessTokenFromIDSRefreshToken(refreshToken);
      console.log('[AuthContext] IDS refresh result:', result);
      return result;
    }
  }; 

  const clearToken = () => { 
    setUser('');
  };

  const setUserRegion = (region:string):void =>{
    if(region){     
      localStorage.setItem(CONSTANTS.user_region, region);
      setRegion(region);
      setIdentityServerUrl(ApplicationSettingService.getIdentityServerEndpoint(region));
      
      ApplicationSettingService.getPlatformIdentityServerEndpoint(region).then((piServer) => {
        setPlatformIdentityServer(piServer);
      }).catch((e) => {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI config not available for region ${region}` : '', MessageType.Info);
      });
      
      LoggingService.log(`AuthContext - UserRegion - ${region}`, MessageType.Info);
    } else {
      LoggingService.log(`AuthContext - setApiUrls - ${CONSTANTS.user_region}: ${userRegion}`, MessageType.Error);
    } 
  };

  const redirectToPlatformIdentity = async (email: string, orgReference: string): Promise<IAuthResult> => {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Redirecting to Platform Identity` : '', MessageType.Info);

    const apiUrl = await ApplicationSettingService.getCarpeApiEndpoint(userRegion);
    const hostClientType = sessionStorage.getItem('hostClientType');
    const oauthRedirectMethod = hostClientType === HostClientType.web ? 'page' : 'deeplink';
    const appInternalId = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_internal_id);

    const baseDomain = ApplicationSettingService.getAppSettingByKey(CONSTANTS.app_url) || window.location.origin;
    const normalizedBase = baseDomain.replace(/\/$/, '');

    const callbackParams = new URLSearchParams();
    if (apiUrl) {
      callbackParams.set('apiUrl', apiUrl);
    }
    if (userRegion) {
      callbackParams.set('region', userRegion);
    }
    callbackParams.set('method', oauthRedirectMethod);
    if (appInternalId) {
      callbackParams.set('authId', appInternalId);
    }

    const callbackUrl = `${window.location.origin}/index.html#/pi-callback?${callbackParams.toString()}`;
    const encodedCallbackUrl = encodeURIComponent(callbackUrl);

    const discoverUrl = `${platformIdentityServer}/auth/discover?organizationHint=${orgReference}&emailHint=${email}&response_type=token`;
    const authStartUrl = `${normalizedBase}/index.html#/auth-start?oauthRedirectMethod=${oauthRedirectMethod}&hostRedirectUrl=${encodedCallbackUrl}&authId=${appInternalId}&identityServerUrl=${encodeURIComponent(discoverUrl)}&loginHint=${encodeURIComponent(email)}&organizationHint=${encodeURIComponent(orgReference)}&identityProvider=${IdentityProvider.PI}`;
    
    return app.initialize().then(() => {
      // Use Teams popup (not external) for better desktop compatibility
      return authentication.authenticate({
        url: authStartUrl,
        width: 600,
        height: 550,
      }).then(async (piToken) => {
        // piToken is a JSON string with authentication result details
        try {
          const result = JSON.parse(piToken);
          
          // Check if the result contains error information
          if (result.errorType) {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI auth failed with error: ${result.errorType}` : '', MessageType.Error);
            return {
              success: false,
              errorType: result.errorType as 'userNotFound' | 'schemaRefreshRunning' | 'authFailed' | 'unknown',
              errorMessage: result.errorMessage
            } as IAuthResult;
          }
          
          // Process successful authentication
          const success = await handlePlatformIdentityCallback(piToken);
          return {
            success: success,
            errorType: success ? undefined : 'authFailed' as const
          } as IAuthResult;
        } catch (e) {
          // If not JSON, treat as raw token (backward compatibility)
          const success = await handlePlatformIdentityCallback(piToken);
          return {
            success: success,
            errorType: success ? undefined : 'authFailed' as const
          } as IAuthResult;
        }
      }).catch((err) => {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI auth error: ${err}` : '', MessageType.Error);
        return {
          success: false,
          errorType: 'unknown' as const,
          errorMessage: err?.message || 'Authentication failed'
        } as IAuthResult;
      });
    });
  };

  const handlePlatformIdentityCallback = async (piToken: string): Promise<boolean> => {
    try {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Processing PI callback` : '', MessageType.Info);
      
      let parsedResult: any;
      
      try {
        parsedResult = JSON.parse(piToken);
      } catch {
        // If not JSON, treat as raw token (shouldn't happen with new flow)
        parsedResult = await SessionService.validatePlatformIdentityUser(piToken);
      }
      
      // Check if this is an error result from PlatformIdentityCallback
      if (parsedResult.success === false) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? 
          `AuthContext - Authentication failed: ${parsedResult.errorType}` : '', 
          MessageType.Error);
        return false;
      }
      
      // This is a successful authentication result with user details
      const userDetail = parsedResult as IUserDetail;
      
      if (userDetail && userDetail.userNotFound) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - User not found in database` : '', MessageType.Error);
        return false;
      }

      if (userDetail && userDetail.schemaRefreshRunning) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Schema refresh is running, please try again later` : '', MessageType.Info);
        return false;
      }
      
      if (userDetail && userDetail.token && userDetail.tcToken) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - User validated: ${userDetail.email}` : '', MessageType.Info);
        
        SessionService.setUserSession(userDetail.email!, IdentityProvider.PI, userDetail.tcToken, userDetail.token);
        
        if (userDetail.refreshToken) {
          sessionStorage.setItem("refreshToken", userDetail.refreshToken);
        }
        
        if (userDetail.orgReference) {
          sessionStorage.setItem("UserOrgReference", userDetail.orgReference);
        }
      
        setUser(userDetail.tcToken);
        setIdentityProvider(IdentityProvider.PI);
        SecurityService.loadPermissionsAndLicenses();
        
        return true;
      }
      
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Invalid user detail response` : '', MessageType.Error);
      return false;
    } catch (error) {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - PI callback error: ${error}` : '', MessageType.Error);
      return false;
    }
  };

  return (<AuthContext.Provider value={{ 
    allRegions, setUserRegion, identityServerUrl, userToken, logout, login, popupLogin, 
    loginCallback, popupLoginCallback, silentCallback, redirectUserToIdp, getToken, 
    getAccessTokenFromRefreshToken, clearToken,
    //Platform Identity
    identityProvider, platformIdentityServer, 
    redirectToPlatformIdentity, handlePlatformIdentityCallback
  }}>{children}</AuthContext.Provider>);
}



function AuthStatus() {
    let auth = useAuth();
    let navigate = useNavigate();
  
    if (!auth.userToken) {
      return <p>You are not logged in.</p>;
    }
  
    return (
      <p>
        Welcome {auth.userToken}!{" "}
        {/* <button onClick={() => { auth.logout(() => navigate("/")); }}>
          Sign out
        </button> */}
      </p>
    );
}

function RequireAuth({ children }: { children: JSX.Element }) {
    let auth = useAuth();
    let location = useLocation();
  
    if (!auth.userToken) {
      LoggingService.log(LoggingService.isLoggingEnabled() ? `AuthContext - Authentication is required. Redirecting user to login screen.` : '', MessageType.Info);
      // Redirect them to the /login page, but save the current location they were
      // trying to go to when they were redirected. This allows us to send them
      // along to that page after they login, which is a nicer user experience
      // than dropping them off on the home page.
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  
    return children;
}

export { useAuth, AuthStatus, RequireAuth, AuthProvider };